import { useEffect, useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, Button } from "@learn-me/ui";
import { useCourseWizard, type GeneratedChapter } from "@/hooks";
import { adminApi } from "../../api/adminApi";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  Clock,
  FileText,
  HelpCircle,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";

export function WizardStep4Generation() {
  const {
    topic,
    level,
    duration,
    additionalNotes,
    courseTitle,
    courseDescription,
    chapters,
    generatedChapters,
    setGeneratedChapters,
    updateGeneratedChapter,
    generationProgress,
    setGenerationProgress,
    isGeneratingContent,
    setIsGeneratingContent,
    currentGeneratingChapter,
    setCurrentGeneratingChapter,
    referenceText,
    // Job tracking (persistido en localStorage)
    currentJobId,
    setCurrentJobId,
    canResumeJob: canResume,
    setCanResumeJob: setCanResume,
    lastCompletedChapterIndex: lastCompletedChapter,
    setLastCompletedChapterIndex: setLastCompletedChapter,
    clearJobState,
  } = useCourseWizard();

  // Initialize generated chapters from wizard chapters
  useEffect(() => {
    if (generatedChapters.length === 0 && chapters.length > 0) {
      setGeneratedChapters(
        chapters.map((ch, index) => ({
          ...ch,
          status: "pending" as const,
        })),
      );
    }
  }, [chapters, generatedChapters.length, setGeneratedChapters]);

  // Verificar estado de job pendiente al montar (por si el usuario cerró el navegador)
  useEffect(() => {
    const checkPendingJob = async () => {
      if (currentJobId && !isGeneratingContent) {
        try {
          const job = await adminApi.getGenerationJob(currentJobId);

          if (job.status === "PROCESSING" || job.status === "PENDING") {
            // Job todavía en proceso, reconectar al polling
            toast.loading("Reconectando a generacion en progreso...", {
              id: "reconnect",
            });
            setIsGeneratingContent(true);

            // Continuar polling
            const success = await pollJobCompletionRef.current?.(currentJobId);
            toast.dismiss("reconnect");

            if (success) {
              toast.success("Generacion completada");
            }
          } else if (job.status === "FAILED" && job.canResume) {
            // Job falló pero puede resumirse
            setCanResume(true);
            const lastCompleted =
              job.rawOutput?._meta?.lastCompletedChapterIndex ?? -1;
            setLastCompletedChapter(lastCompleted);

            // Actualizar estado visual de capítulos
            setGeneratedChapters(
              chapters.map((ch, index) => ({
                ...ch,
                status:
                  index <= lastCompleted
                    ? ("completed" as const)
                    : ("error" as const),
                errorMessage:
                  index > lastCompleted ? "Error en la generacion" : undefined,
              })),
            );

            toast.error(
              `Generacion anterior interrumpida. Puedes reanudar desde el capitulo ${lastCompleted + 2}.`,
            );
          } else if (job.status === "COMPLETED") {
            // Job ya completado, limpiar estado
            clearJobState();
          } else {
            // Job falló sin checkpoint, limpiar
            clearJobState();
          }
        } catch (error) {
          // Job no encontrado, limpiar estado
          console.error("Error checking pending job:", error);
          clearJobState();
        }
      }
    };

    checkPendingJob();
    // Solo ejecutar al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Referencia a pollJobCompletion para usar en useEffect
  const pollJobCompletionRef = useRef<
    ((jobId: string) => Promise<boolean>) | null
  >(null);

  // Función común para hacer polling de un job
  const pollJobCompletion = useCallback(
    async (jobId: string) => {
      let completed = false;
      let attempts = 0;
      const maxAttempts = 300; // 10 minutes max (2s interval)

      while (!completed && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        attempts++;

        const job = await adminApi.getGenerationJob(jobId);

        // Update progress
        const progress = Math.round((job.progress / 100) * 100);
        setGenerationProgress(progress);

        // Estimate which chapter is being generated
        const estimatedChapter = Math.min(
          Math.floor((job.progress / 100) * chapters.length),
          chapters.length - 1,
        );
        setCurrentGeneratingChapter(estimatedChapter);

        // Update chapter statuses
        setGeneratedChapters(
          chapters.map((ch, index) => ({
            ...ch,
            status:
              index < estimatedChapter
                ? "completed"
                : index === estimatedChapter
                  ? "generating"
                  : "pending",
          })),
        );

        if (job.status === "COMPLETED") {
          completed = true;
          setGenerationProgress(100);
          setCanResume(false);
          setCurrentJobId(null);

          // Obtener chapters con sections y quiz del job
          const jobChapters = job.chapters || [];

          console.log(
            "[Wizard Step 4] Job completed, chapters from backend:",
            jobChapters.length,
          );
          jobChapters.forEach((ch, i) => {
            console.log(`  Chapter ${i + 1}: ${ch.title}`);
            console.log(`    - Sections: ${ch.sections?.length || 0}`);
            console.log(`    - Quiz: ${ch.quiz ? "Yes" : "No"}`);
          });

          setGeneratedChapters(
            chapters.map((ch, index) => {
              const generated = jobChapters[index];
              return {
                ...ch,
                status: "completed" as const,
                sections: generated?.sections || [],
                quiz: generated?.quiz,
              };
            }),
          );

          toast.success("Contenido generado exitosamente");
          return true;
        } else if (job.status === "FAILED") {
          // Verificar si se puede resumir
          if (job.canResume) {
            setCanResume(true);
            setCurrentJobId(jobId);
            const lastCompleted =
              job.rawOutput?._meta?.lastCompletedChapterIndex ?? -1;
            setLastCompletedChapter(lastCompleted);

            // Actualizar estado de capítulos para mostrar cuáles se completaron
            setGeneratedChapters(
              chapters.map((ch, index) => ({
                ...ch,
                status:
                  index <= lastCompleted
                    ? ("completed" as const)
                    : ("error" as const),
                errorMessage:
                  index > lastCompleted ? "Error en la generacion" : undefined,
              })),
            );

            toast.error(
              `Error en la generacion. ${lastCompleted + 1} capitulos completados. Puedes reanudar desde donde quedo.`,
            );
          } else {
            throw new Error(job.errorMessage || "Error en la generacion");
          }
          return false;
        }
      }

      if (!completed) {
        throw new Error("La generacion tardo demasiado tiempo");
      }
      return false;
    },
    [
      chapters,
      setGenerationProgress,
      setCurrentGeneratingChapter,
      setGeneratedChapters,
    ],
  );

  // Actualizar ref para uso en useEffect de reconexión
  useEffect(() => {
    pollJobCompletionRef.current = pollJobCompletion;
  }, [pollJobCompletion]);

  const startGeneration = useCallback(async () => {
    setIsGeneratingContent(true);
    setCanResume(false);

    try {
      const result = await adminApi.generateCourse({
        topic,
        duration,
        level,
        language: "es",
        includeQuizzes: chapters.some((ch) => ch.includeQuiz),
        additionalNotes: additionalNotes || undefined,
        chapters: chapters.map((ch) => ({
          title: ch.title,
          description: ch.description || "",
          estimatedDuration: ch.estimatedDuration,
          includeQuiz: ch.includeQuiz,
        })),
      });

      setCurrentJobId(result.jobId);
      await pollJobCompletion(result.jobId);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al generar contenido",
      );
      if (!canResume) {
        setGeneratedChapters(
          chapters.map((ch) => ({
            ...ch,
            status: "error" as const,
            errorMessage: "Error en la generacion",
          })),
        );
      }
    } finally {
      setIsGeneratingContent(false);
      setCurrentGeneratingChapter(null);
    }
  }, [
    topic,
    duration,
    level,
    additionalNotes,
    chapters,
    canResume,
    pollJobCompletion,
    setIsGeneratingContent,
    setCurrentGeneratingChapter,
    setGeneratedChapters,
  ]);

  // Función para reanudar generación desde checkpoint
  const resumeGeneration = useCallback(async () => {
    if (!currentJobId) return;

    setIsGeneratingContent(true);
    setCanResume(false);

    try {
      toast.loading("Reanudando generacion...", { id: "resume" });
      await adminApi.resumeGenerationJob(currentJobId);
      toast.dismiss("resume");
      toast.success(`Reanudando desde capitulo ${lastCompletedChapter + 2}...`);

      await pollJobCompletion(currentJobId);
    } catch (error) {
      toast.dismiss("resume");
      toast.error(error instanceof Error ? error.message : "Error al reanudar");
    } finally {
      setIsGeneratingContent(false);
      setCurrentGeneratingChapter(null);
    }
  }, [
    currentJobId,
    lastCompletedChapter,
    pollJobCompletion,
    setIsGeneratingContent,
    setCurrentGeneratingChapter,
  ]);

  // Función para reintentar un capítulo específico
  const retryChapter = useCallback(
    async (chapterIndex: number) => {
      if (!currentJobId) {
        toast.error("No hay un job activo para reintentar");
        return;
      }

      // Actualizar estado visual
      updateGeneratedChapter(chapterIndex, {
        status: "generating",
        errorMessage: undefined,
      });

      try {
        toast.loading(`Regenerando capítulo ${chapterIndex + 1}...`, {
          id: `retry-${chapterIndex}`,
        });

        const result = await adminApi.retryChapter(currentJobId, chapterIndex);

        toast.dismiss(`retry-${chapterIndex}`);
        toast.success(result.message);

        // Actualizar estado del capítulo
        updateGeneratedChapter(chapterIndex, {
          status: "completed",
          errorMessage: undefined,
        });

        // Si todos los capítulos están completos, actualizar progreso
        if (result.allChaptersComplete) {
          setGenerationProgress(100);
          setCanResume(false);
        }
      } catch (error) {
        toast.dismiss(`retry-${chapterIndex}`);
        toast.error(
          error instanceof Error
            ? error.message
            : "Error al regenerar capítulo",
        );

        updateGeneratedChapter(chapterIndex, {
          status: "error",
          errorMessage: "Error al regenerar",
        });
      }
    },
    [currentJobId, updateGeneratedChapter, setGenerationProgress, setCanResume],
  );

  const allCompleted = generatedChapters.every(
    (ch) => ch.status === "completed",
  );
  const hasErrors = generatedChapters.some((ch) => ch.status === "error");
  const notStarted = generatedChapters.every((ch) => ch.status === "pending");

  return (
    <div className="space-y-6">
      {/* Progress header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-900">
                Generacion de contenido
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {allCompleted
                  ? "Todo el contenido ha sido generado"
                  : isGeneratingContent
                    ? `Generando capitulo ${(currentGeneratingChapter ?? 0) + 1} de ${chapters.length}...`
                    : "Listo para generar el contenido"}
              </p>
            </div>
            {!isGeneratingContent && !allCompleted && (
              <div className="flex gap-2">
                {canResume && (
                  <Button onClick={resumeGeneration} variant="default">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reanudar ({lastCompletedChapter + 1}/{chapters.length}{" "}
                    listos)
                  </Button>
                )}
                <Button
                  onClick={startGeneration}
                  variant={canResume ? "outline" : "default"}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {hasErrors && !canResume
                    ? "Reintentar"
                    : canResume
                      ? "Reiniciar desde cero"
                      : "Iniciar generacion"}
                </Button>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-500"
              style={{ width: `${generationProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2 text-center">
            {generationProgress}% completado
          </p>
        </CardContent>
      </Card>

      {/* Chapters list */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Capitulos</h3>

          <div className="space-y-3">
            {generatedChapters.map((chapter, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 transition-all ${
                  chapter.status === "generating"
                    ? "border-primary-300 bg-primary-50"
                    : chapter.status === "completed"
                      ? "border-green-200 bg-green-50"
                      : chapter.status === "error"
                        ? "border-red-200 bg-red-50"
                        : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {chapter.status === "generating" ? (
                      <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
                    ) : chapter.status === "completed" ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : chapter.status === "error" ? (
                      <XCircle className="h-5 w-5 text-red-500" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                    )}
                  </div>

                  {/* Chapter info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        Capitulo {index + 1}:
                      </span>
                      <span className="text-sm text-gray-900 truncate">
                        {chapter.title}
                      </span>
                    </div>
                    {chapter.status === "generating" && (
                      <p className="text-xs text-primary-600 mt-1">
                        Generando contenido y secciones...
                      </p>
                    )}
                    {chapter.status === "error" && chapter.errorMessage && (
                      <p className="text-xs text-red-600 mt-1">
                        {chapter.errorMessage}
                      </p>
                    )}
                  </div>

                  {/* Metadata and Retry button */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {chapter.estimatedDuration}m
                    </span>
                    {chapter.includeQuiz && (
                      <span className="flex items-center gap-1">
                        <HelpCircle className="h-3 w-3" />
                        Quiz
                      </span>
                    )}
                    {/* Retry button for failed chapters */}
                    {chapter.status === "error" &&
                      currentJobId &&
                      !isGeneratingContent && (
                        <button
                          type="button"
                          onClick={() => retryChapter(index)}
                          className="ml-2 flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                          title="Reintentar este capítulo"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Reintentar
                        </button>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Generation info */}
      {isGeneratingContent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">
                Generacion en progreso
              </p>
              <p className="text-sm text-blue-700 mt-1">
                La AI esta creando el contenido de cada capitulo, incluyendo
                secciones de microlearning y quizzes. Este proceso puede tomar
                varios minutos dependiendo del numero de capitulos.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Resume available notice */}
      {canResume && !isGeneratingContent && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Generacion interrumpida - Checkpoint disponible
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Se completaron {lastCompletedChapter + 1} de {chapters.length}{" "}
                capitulos antes del error. Puedes{" "}
                <strong>reanudar la generacion</strong> desde donde quedo para
                no perder el progreso, o reiniciar desde cero.
              </p>
            </div>
          </div>
        </div>
      )}

      {allCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Generacion completada
              </p>
              <p className="text-sm text-green-700 mt-1">
                Todo el contenido ha sido generado exitosamente. Puedes
                continuar al siguiente paso para revisar y ajustar el contenido.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
